
import { useState } from 'react';
import { TrainingFormValues } from '../../../types';
import { useTrainingJournal } from '../../../context/useTrainingJournal';
import { toast } from 'sonner';
import { clearDraft } from '../../../utils/draftStorage';
import { savePersistentFields, clearFormDraft } from '../../../utils/storageUtils';

interface UseTrainingFormProps {
  initialValues?: Partial<TrainingFormValues>;
  onSubmit?: (data: TrainingFormValues) => void;
  isEditing?: boolean;
  sessionId?: string;
}

export const useTrainingForm = ({
  onSubmit,
  isEditing = false,
  sessionId,
}: UseTrainingFormProps) => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { addSession, editSession } = useTrainingJournal();

  const parseErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      const message = error.message;
      
      // Check for YouTube URL validation errors
      if (message.includes('Invalid YouTube URL format')) {
        return 'Video URL must be from YouTube (youtube.com/watch?v=... or youtu.be/...), Vimeo, or other supported platforms. Please check the format and try again.';
      }
      
      // Check for other specific database errors
      if (message.includes('duplicate key value')) {
        return 'A session with this information already exists. Please check your entries.';
      }
      
      if (message.includes('foreign key violation')) {
        return 'There was an issue with the session data. Please refresh the page and try again.';
      }
      
      if (message.includes('not-null constraint')) {
        return 'All required fields must be filled out. Please check your form and try again.';
      }
      
      // Return the original error message if it's user-friendly
      if (message.length < 100 && !message.includes('function') && !message.includes('SQL')) {
        return message;
      }
    }
    
    // Generic fallback
    return 'Failed to save training session. Please check your entries and try again.';
  };

  const handleSubmit = async (data: TrainingFormValues) => {

    setIsSubmittingForm(true);
    try {
      if (isEditing && sessionId) {

        await editSession(sessionId, data);
        toast.success('Training session updated successfully!');
      } else {

        await addSession(data);

        toast.success('Training session added successfully!');
        
        // Save persistent fields for next session (only for new sessions)
        savePersistentFields({
          sessionType: data.sessionType,
          duration: data.duration,
          professor: data.professor,
          location: data.location
        });
        
        // Clear the draft since submission was successful
        clearDraft();
        clearFormDraft();

      }

      if (onSubmit) {

        onSubmit(data);
      }
    } catch (error) {
      // Use the enhanced error parsing
      const userFriendlyMessage = parseErrorMessage(error);
      toast.error(userFriendlyMessage);
    } finally {
      setIsSubmittingForm(false);
    }
  };

  return {
    isSubmittingForm,
    handleSubmit,
  };
};
